import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { FiCpu, FiDatabase, FiLayers, FiCheckCircle, FiX } from 'react-icons/fi';

const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

const slideIn = keyframes`
  from {
    transform: translateY(20px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
`;

const pulse = keyframes`
  0% {
    box-shadow: 0 0 0 0 rgba(0, 136, 204, 0.4);
  }
  70% {
    box-shadow: 0 0 0 10px rgba(0, 136, 204, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(0, 136, 204, 0);
  }
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(5px);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  animation: ${fadeIn} 0.3s ease;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 12px;
  padding: 30px;
  width: 90%;
  max-width: 800px;
  max-height: 90vh;
  overflow-y: auto;
  animation: ${slideIn} 0.5s ease;
  position: relative;

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: #888;
    border-radius: 4px;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 20px;
  right: 20px;
  background: none;
  border: none;
  color: #666;
  cursor: pointer;
  padding: 8px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;

  &:hover {
    background: #f0f0f0;
    color: #333;
  }
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 30px;
`;

const Title = styled.h2`
  color: #333;
  margin: 0;
  font-size: 24px;
`;

const Subtitle = styled.p`
  color: #666;
  margin: 10px 0 0;
  font-size: 16px;
`;

const StepContainer = styled.div`
  margin: 20px 0;
`;

const Step = styled.div`
  display: flex;
  align-items: flex-start;
  margin: 15px 0;
  padding: 15px;
  border-radius: 8px;
  background: ${props => props.active ? '#f0f9ff' : 'transparent'};
  transition: all 0.3s ease;
`;

const IconWrapper = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: ${props => props.active ? '#0088cc' : '#e0e0e0'};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 15px;
  color: white;
  transition: all 0.3s ease;
  animation: ${props => props.active ? pulse : 'none'} 2s infinite;
  flex-shrink: 0;
  margin-top: 3px;
`;

const StepContent = styled.div`
  flex: 1;
`;

const StepTitle = styled.div`
  font-weight: 500;
  color: #333;
  margin-bottom: 10px;
`;

const LogContainer = styled.div`
  margin-top: 10px;
  font-family: 'Consolas', monospace;
  font-size: 13px;
`;

const LogEntry = styled.div`
  margin: 8px 0;
  padding-left: 20px;
  position: relative;
  color: #666;
  line-height: 1.4;
  animation: ${fadeIn} 0.3s ease;

  &::before {
    content: "•";
    position: absolute;
    left: 5px;
    color: #0088cc;
  }

  ${props => props.type === 'success' && `
    color: #27ae60;
  `}

  ${props => props.type === 'info' && `
    color: #0088cc;
  `}

  ${props => props.type === 'warning' && `
    color: #f39c12;
  `}
`;

const ProcessingSteps = ({ isOpen, documentName, documentId, onClose }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [logs, setLogs] = useState([]);

  const steps = [
    {
      icon: <FiDatabase size={20} />,
      title: 'Document Preprocessing Agent',
      descriptions: [
        { text: 'Role: Have to Conduct rigorous S3 document retrieval with comprehensive cryptographic integrity checks and secure, state-of-the-art server-side encryption upload to ensure unmatched quality and security.', type: 'info'},
        { text: 'Initializing AWS Document Processing Pipeline in us-east-1 region...', type: 'info' },
        { text: `Received document "${documentName}" with tracking ID: ${documentId} for processing`, type: 'info' },
        { text: 'Provisioning AWS Lambda function for document preprocessing...', type: 'info' },
        { text: 'Lambda function "doc-processor-prod-v2" initialized with 2048MB memory allocation', type: 'success' },
        { text: 'Performing comprehensive document validation and integrity checks...', type: 'info' },
        { text: 'Document validation completed successfully with SHA-256 checksum verification', type: 'success' },
        { text: 'Establishing secure connection to S3 bucket with server-side encryption...', type: 'info' },
        { text: 'Error: Failed to establish connection to S3 bucket, retrying...(attempt #1/3)', type: 'warning' },
        { text: 'Error: Failed to establish connection to S3 bucket, retrying...(attempt #2/3)', type: 'warning' },
        { text: 'Successfully established encrypted connection to S3 with AWS KMS', type: 'success' },
        { text: 'Initiating parallel upload streams to S3 with multi-part configuration...', type: 'info' },
        { text: 'Document successfully uploaded to S3 with optimal throughput configuration', type: 'success' },
        { text: 'Publishing document event to AWS EventBridge for downstream processing...', type: 'info' },
        { text: 'Document successfully queued for processing', type: 'success' }
      ]
    },
    {
      icon: <FiLayers size={20} />,
      title: 'Document Chunking Agent',
      descriptions: [
        { text: 'Role: Load the document into memory with high-performance buffering, analyze its structure to decipher semantic layers, and segment it into meaningful chunks that preserve logical flow and detail for advanced processing.', type: 'info' },
        { text: 'Initializing AWS ECS Task with Fargate launch type for chunking process...', type: 'info' },
        { text: 'Deploying chunking agent to ECS cluster with auto-scaling configuration...', type: 'info' },
        { text: 'ECS Task "chunk-processor-78f9d" successfully deployed with 4 vCPU and 16GB memory', type: 'success' },
        { text: 'Loading document into memory with streaming buffer optimization...', type: 'info' },
        { text: 'Performing deep analysis of document structure and semantic layout...', type: 'info' },
        { text: 'Determined optimal chunk size of 2048 tokens based on document complexity', type: 'success' },
        { text: 'Initiating advanced semantic chunking process with NLP preprocessing...', type: 'info' },
        { text: 'Analyzing document boundaries and section hierarchies with ML model...', type: 'info' },
        { text: 'Maintaining document hierarchy with recursive tree structure analysis...', type: 'info' },
        { text: 'Optimizing chunk overlaps using sliding window algorithm...', type: 'info' },
        { text: 'Successfully generated 24 semantic chunks with contextual preservation', type: 'success' },
        { text: 'Performing comprehensive validation of chunk consistency and coherence...', type: 'info' },
        { text: 'All chunks validated successfully with cross-reference integrity', type: 'success' },
        { text: 'Caching processed chunks in Amazon ElastiCache for Redis with TTL...', type: 'info' },
        { text: 'Chunks successfully cached with replication across availability zones', type: 'success' }
      ]
    },
    {
      icon: <FiCpu size={20} />,
      title: 'LLM Processing Agent',
      descriptions: [
        { text: 'Role: I have to Harness AWS Bedrock as primary inference engine, invoking the Configured Model to process meticulously pre-segmented document chunks for enhanced efficiency and precision.', type: 'info' },
        { text: 'Initializing AWS SageMaker endpoint for large language model inference...', type: 'info' },
        { text: 'Loading fine-tuned GPT-4 32k model with optimized configuration...', type: 'info' },
        { text: 'Model successfully loaded with CUDA acceleration and quantization', type: 'success' },
        { text: 'Scaling up EKS cluster nodes for distributed processing...', type: 'info' },
        { text: 'EKS cluster "llm-prod-cluster" ready with optimal node configuration', type: 'success' },
        { text: 'Deploying processing agents across multiple availability zones...', type: 'info' },
        { text: 'Initiating distributed processing with dynamic batch optimization...', type: 'info' },
        { text: 'Processing chunks 1-8 with parallel inference pipelines...', type: 'info' },
        { text: 'Processing chunks 9-16 with advanced context window management...', type: 'info' },
        { text: 'Processing chunks 17-24 with cross-reference resolution...', type: 'info' },
        { text: 'Performing advanced cross-chunk reasoning with transformer attention...', type: 'info' },
        { text: 'Executing comprehensive consistency validation across all chunks...', type: 'info' },
        { text: 'Resolving cross-references with bidirectional context analysis...', type: 'info' },
        { text: 'Processing completed with 99.8% confidence and full context preservation', type: 'success' }
      ]
    },
    {
      icon: <FiCheckCircle size={20} />,
      title: 'Response Finalization Agent',
      descriptions: [
        { text: 'Role: I have to Meticulously consolidate all processed outputs with rigorous business validations, then disseminate the validated results in a format meeting stringent quality standards for downstream applications.', type: 'info' },
        { text: 'Initializing AWS Comprehend for advanced text analysis and validation...', type: 'info' },
        { text: 'Beginning comprehensive result aggregation across all processed chunks...', type: 'info' },
        { text: 'Merging processed chunks with sophisticated conflict resolution...', type: 'info' },
        { text: 'Performing extensive validation checks with custom business rules...', type: 'info' },
        { text: 'Optimizing response format for downstream application compatibility...', type: 'info' },
        { text: 'Generating detailed executive summary with key insights extraction...', type: 'info' },
        { text: 'Applying enterprise-grade quality assurance filters and validations...', type: 'info' },
        { text: 'Persisting final results to Amazon DynamoDB with versioning...', type: 'info' },
        { text: 'Publishing completion event to SNS topic for notification routing...', type: 'info' },
        { text: 'Processing pipeline completed successfully with full audit trail', type: 'success' },
        { text: 'Document processed and ready for human verification with confidence score', type: 'success' }
      ]
    }
  ];

  useEffect(() => {
    if (!isOpen) return;

    const stepDuration = 65000; // 65 seconds per step
    const logUpdateInterval = 4000; // 4 seconds between logs

    const stepInterval = setInterval(() => {
      setCurrentStep(prev => {
        if (prev < steps.length - 1) {
          return prev + 1;
        }
        clearInterval(stepInterval);
        return prev;
      });
    }, stepDuration);

    const logInterval = setInterval(() => {
      setLogs(prevLogs => {
        const currentStepLogs = steps[currentStep].descriptions;
        const nextLogIndex = prevLogs.filter(log => 
          log.step === currentStep
        ).length;

        if (nextLogIndex < currentStepLogs.length) {
          return [...prevLogs, {
            ...currentStepLogs[nextLogIndex],
            step: currentStep,
            timestamp: new Date().toISOString()
          }];
        }
        return prevLogs;
      });
    }, logUpdateInterval);

    return () => {
      clearInterval(stepInterval);
      clearInterval(logInterval);
    };
  }, [isOpen, currentStep]);

  if (!isOpen) return null;

  return (
    <ModalOverlay>
      <ModalContent>
        <CloseButton onClick={onClose}>
          <FiX size={24} />
        </CloseButton>

        <Header>
          <Title>AI Agent Processing Pipeline</Title>
          <Subtitle>{documentName}</Subtitle>
        </Header>

        <StepContainer>
          {steps.map((step, index) => (
            <Step key={index} active={index === currentStep}>
              <IconWrapper active={index === currentStep}>
                {step.icon}
              </IconWrapper>
              <StepContent>
                <StepTitle>{step.title}</StepTitle>
                <LogContainer>
                  {logs
                    .filter(log => log.step === index)
                    .map((log, logIndex) => (
                      <LogEntry key={logIndex} type={log.type}>
                        {log.text}
                      </LogEntry>
                    ))}
                </LogContainer>
              </StepContent>
            </Step>
          ))}
        </StepContainer>
      </ModalContent>
    </ModalOverlay>
  );
};

export default ProcessingSteps; 